import Head from "next/head";
import React, { useState, useEffect } from "react";
import { Bars3BottomLeftIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import useSWRInfinite from "swr/infinite";
import { useRouter } from "next/router";
import Sidebar from "components/sidebar";
import AnimalCard from "components/animalCard";
import BlankCard from "components/blankCard";
import SearchFilter from "components/filter";

const fetcher = (url) => fetch(url, { credentials: "include" }).then((res) => res.json());

export const getServerSideProps = async ({ query }) => ({
	props: {
		defaultQuery: query.query || query.animal || null,
		defaultRisk: query.risk ? query.risk.split(",") : [],
		defaultOrder: query.order ? query.order.split(",") : [],
		defaultHabitat: query.habitat ? query.habitat.split(",") : [],
	},
});

export default function Data({ defaultQuery, defaultRisk, defaultOrder, defaultHabitat }) {
	const [animal, setAnimal] = useState(defaultQuery);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [query, setQuery] = useState(defaultQuery || "");

	const [riskFilters, setRiskFilters] = useState(defaultRisk);
	const [orderFilters, setOrderFilters] = useState(defaultOrder);
	const [habitatFilters, setHabitatFilters] = useState(defaultHabitat);
	const [queryString, setQueryString] = useState("");

	const getKey = (pageIndex, previousPageData) => {
		if (previousPageData && !previousPageData.nextOffset) return null;
		if (pageIndex === 0) return `/api/search?scoreSort=true&${queryString}`;
		return `/api/search?offset=${previousPageData.nextOffset}&scoreSort=true&${queryString}`;
	};

	const { data: animalData, error, size, setSize } = useSWRInfinite(getKey, fetcher);
	const [animals, setAnimals] = useState(
		animalData && animalData.length > 0 && animalData[0].data ? animalData[0].data : []
	);
	const [orderData, setOrderData] = useState(
		animalData && animalData.length > 0 && animalData[0].orderData ? animalData[0].orderData : []
	);

	useEffect(() => {
		const params = { query };
		if (riskFilters.length !== 0) params.risk = riskFilters.join();
		if (orderFilters.length !== 0) params.order = orderFilters.join();
		if (habitatFilters.length !== 0) params.habitat = habitatFilters.join();
		setQueryString(new URLSearchParams(params).toString());
	}, [riskFilters, orderFilters, habitatFilters, query]);

	useEffect(() => {
		if (!animalData) return;
		setAnimals(animalData.reduce((prev, page) => prev.concat(page.data ?? []), []));
		setOrderData(animalData[0].orderData);
	}, [animalData]);

	const router = useRouter();
	const queryUpdated = (q) => {
		const params = {};

		if (q) params.query = q;

		if (riskFilters.length !== 0) params.risk = riskFilters.join();

		if (orderFilters.length !== 0) params.order = orderFilters.join();

		if (habitatFilters.length !== 0) params.habitat = habitatFilters.join();
		router.push(`/data?${new URLSearchParams(params).toString()}`, undefined, { shallow: true });
	};

	useEffect(() => {
		queryUpdated(query);
	}, [riskFilters, orderFilters, habitatFilters]);

	const handleScroll = (evt) => {
		if (
			document.getElementById("scrollRef") &&
			(evt.target.scrollTop || window.innerHeight + window.scrollY) >=
				document.getElementById("scrollRef").scrollHeight - 1500 &&
			!((!animalData && !error) || (size > 0 && animalData && typeof animalData[size - 1] === "undefined"))
		) {
			console.log(`Fetching more data...${size + 1}`);
			setSize(size + 1);
		}
	};

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	});

	return (
		<div>
			<Head>
				<title>Database | Cary Dashboard</title>
				<meta name="description" content="Cary Dashboard" />
			</Head>

			<div className="overflow-auto bg-gray-100">
				<SkeletonTheme>
					<Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} page={"Database"} />
					<div className="flex flex-1 flex-col md:pl-64">
						<div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
							<button
								type="button"
								onClick={() => setSidebarOpen(true)}
								className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cary-400 md:hidden">
								<span className="sr-only">Open sidebar</span>
								<Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
							</button>
							<div className="flex flex-1 justify-between px-4">
								<div className="flex flex-1">
									<form className="flex w-full md:ml-0" action="#" method="GET">
										<label htmlFor="search-field" className="sr-only">
											Search
										</label>
										<div className="relative w-full text-gray-400 focus-within:text-gray-600">
											<button className="absolute inset-y-0 left-0 flex items-center">
												<MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
											</button>
											<input
												onChange={(event) => {
													setQuery(event.target.value);
													queryUpdated(event.target.value);
												}}
												value={query}
												placeholder="Search"
												className="block h-full w-full border-transparent py-2 pl-8 pr-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
											/>
										</div>
									</form>
								</div>
							</div>
						</div>

						<main className="flex-1 overflow-auto" onScroll={handleScroll} id="scrollRef">
							<div className="">
								<div className="mx-auto">
									<div className="">
										<div className="height-100vh w-full py-6 2xl:pt-24">
											<h1 className="pt-4 pb-2 text-center text-2xl font-bold">
												{animals.length === 0 &&
												query === "" &&
												riskFilters.length === 0 &&
												orderFilters.length === 0 &&
												habitatFilters.length === 0 ? (
													<Skeleton height={30} width={400} />
												) : (
													`${animalData?.[0]?.totalResults ?? 0} Animals ${
														query === "" && riskFilters.length === 0 && orderFilters.length === 0
															? "Documented"
															: "Found"
													}`
												)}
											</h1>
											<SearchFilter
												riskFilters={riskFilters}
												setRiskFilters={setRiskFilters}
												orderFilters={orderFilters}
												setOrderFilters={setOrderFilters}
												habitatFilters={habitatFilters}
												setHabitatFilters={setHabitatFilters}
												page={"database"}
												orderData={orderData}>
												{animals.length === 0 &&
												query === "" &&
												riskFilters.length === 0 &&
												orderFilters.length === 0 &&
												habitatFilters.length === 0 ? (
													<Skeleton height={20} width={400} />
												) : (
													<>Select or search for a species to receive more information.</>
												)}
											</SearchFilter>

											<div className="mx-6 2xl:mx-12">
												{animals && animals.length > 0 && (
													<>
														<ul
															role="list"
															className="mt-4 grid grid-cols-1 place-content-center gap-6 xl:grid-cols-2 2xl:grid-cols-3">
															{animals.map((anim) => (
																<AnimalCard key={anim.name} animal={anim} openIf={animal} setAnimal={setAnimal} />
															))}
														</ul>
													</>
												)}

												{(!animals || animals.length === 0) && (
													<>
														<ul
															role="list"
															className="mt-4 grid grid-cols-1 place-content-center gap-6 xl:grid-cols-2 2xl:grid-cols-3">
															{Array(12)
																.fill(0)
																.map((_, i) => (
																	<BlankCard key={`Blank-${i}`} />
																))}
														</ul>
													</>
												)}

												{size * 24 < animalData?.[0]?.totalResults && (
													<button
														onClick={() => setSize(size + 1)}
														className="relative my-4 -mr-px inline-flex w-full flex-1 items-center justify-center rounded-lg rounded-bl-lg border-2 border-transparent bg-gray-200 py-2 px-4 text-center text-sm font-medium text-gray-800">
														{(!animalData && !error) ||
														(size > 0 && animalData && typeof animalData[size - 1] === "undefined") ? (
															<>
																<svg
																	className="mr-2 h-5 w-5 animate-spin text-black"
																	xmlns="http://www.w3.org/2000/svg"
																	fill="none"
																	viewBox="0 0 24 24">
																	<circle
																		className="opacity-25"
																		cx="12"
																		cy="12"
																		r="10"
																		stroke="currentColor"
																		strokeWidth="4"></circle>
																	<path
																		className="opacity-75"
																		fill="currentColor"
																		d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
																</svg>{" "}
																Loading
															</>
														) : (
															`Load More (Size: ${size})`
														)}
													</button>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</main>
					</div>
				</SkeletonTheme>
			</div>
		</div>
	);
}
